
(() => {
  "use strict";

  const root = document.querySelector("[data-anat-cart-root]");
  if (!root) return;

  const globalError = root.querySelector("[data-cart-global-error]");

  const state = {
    pending: new Map(),
    checkoutPending: false,
    cart: null
  };

  const route = (path) =>
    `${window.Shopify?.routes?.root || "/"}${path}`.replace(/\/{2,}/g, "/");

  function showGlobalError(message) {
    if (!globalError) return;
    globalError.textContent = message;
    globalError.hidden = false;
  }

  function clearGlobalError() {
    if (!globalError) return;
    globalError.textContent = "";
    globalError.hidden = true;
  }

  function showLineError(row, message) {
    const el = row?.querySelector("[data-line-error]");
    if (!el) return showGlobalError(message);
    el.textContent = message;
    el.hidden = false;
  }

  function clearLineError(row) {
    const el = row?.querySelector("[data-line-error]");
    if (!el) return;
    el.textContent = "";
    el.hidden = true;
  }

  function formatMoney(cents, currency) {
    const amount = Number(cents || 0) / 100;
    const code =
      currency ||
      state.cart?.currency ||
      window.Shopify?.currency?.active ||
      "RON";

    try {
      return new Intl.NumberFormat("ro-RO", {
        style: "currency",
        currency: code
      }).format(amount);
    } catch (_) {
      return `${amount.toFixed(2)} ${code}`;
    }
  }

  function setBusy(row, busy) {
    if (!row) return;
    row.classList.toggle("is-updating", busy);
    row.querySelectorAll("button,input").forEach((el) => {
      el.disabled = !!busy;
    });
  }

  function updateHeaderCount(count) {
    const bubble = document.getElementById("cart-count");
    if (bubble) bubble.textContent = count;

    document.querySelectorAll("[data-cart-count]").forEach((el) => {
      el.textContent = count;
    });

    const headCount = root.querySelector("[data-cart-count-text]");
    if (headCount) headCount.textContent = count;

    const summaryCount = root.querySelector("[data-summary-count]");
    if (summaryCount) summaryCount.textContent = count;
  }

  async function fetchCart() {
    const res = await fetch(route("cart.js"), {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      cache: "no-store"
    });

    const data = await res.json().catch(() => null);

    if (!res.ok || !data) {
      throw new Error(
        data?.description ||
        data?.message ||
        `Nu am putut citi coșul (HTTP ${res.status}).`
      );
    }

    state.cart = data;
    return data;
  }

  function syncCartUI(cart) {
    state.cart = cart;
    updateHeaderCount(cart.item_count);

    const total = root.querySelector("[data-cart-total]");
    if (total) total.textContent = formatMoney(cart.total_price, cart.currency);

    const rows = [...root.querySelectorAll("[data-cart-line]")];
    const byKey = new Map(cart.items.map((item) => [item.key, item]));

    rows.forEach((row) => {
      const key = row.dataset.key;
      const item = byKey.get(key);

      if (!item) {
        row.classList.add("is-removing");
        window.setTimeout(() => row.remove(), 180);
        return;
      }

      const input = row.querySelector("[data-qty-input]");
      if (input && Number(input.value) !== item.quantity) {
        input.value = item.quantity;
      }

      const price = row.querySelector("[data-line-price]");
      if (price) {
        price.textContent = formatMoney(item.final_line_price, cart.currency);
      }
    });

    if (cart.item_count === 0) {
      window.location.reload();
    }
  }

  async function changeItem(row, requestedQty) {
    if (!row || !row.dataset.key) return;

    const key = row.dataset.key;
    if (state.pending.has(key)) return;

    const input = row.querySelector("[data-qty-input]");
    const previousQty = Number(input?.value || 1);

    let qty = Number(requestedQty);
    if (!Number.isFinite(qty)) qty = previousQty;

    const min = Number(input?.min || 1);
    const htmlMax = input?.max ? Number(input.max) : 20;
    const otherItemsQty = Math.max(0, (Number(state.cart?.item_count) || 0) - previousQty);
    const cartRemainingForLine = Math.max(0, 20 - otherItemsQty);
    const max = Math.min(Number.isFinite(htmlMax) ? htmlMax : 20, cartRemainingForLine);
    const step = Number(input?.step || 1) || 1;

    if (qty !== 0) {
      if (qty > max) {
        showLineError(row, "Poți avea maximum 20 de produse în coș.");
      }
      qty = Math.max(min, qty);

      if (max !== null && Number.isFinite(max)) {
        qty = Math.min(max, qty);
      }

      if (step > 1) {
        qty = min + Math.round((qty - min) / step) * step;

        if (max !== null && Number.isFinite(max)) {
          qty = Math.min(max, qty);
        }
      }
    }

    clearGlobalError();
    clearLineError(row);
    setBusy(row, true);
    state.pending.set(key, true);

    try {
      const res = await fetch(route("cart/change.js"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        credentials: "same-origin",
        body: JSON.stringify({
          id: key,
          quantity: qty
        })
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data.description ||
          data.message ||
          `Shopify a refuzat actualizarea (HTTP ${res.status}).`
        );
      }

      syncCartUI(data);

      document.dispatchEvent(
        new CustomEvent("anatomik:cart-updated", {
          detail: {
            cart: data,
            key,
            quantity: qty
          }
        })
      );
    } catch (err) {
      if (input) input.value = previousQty;

      const message =
        err?.message ||
        "A apărut o eroare la actualizarea coșului.";

      showLineError(row, message);

      try {
        const canonical = await fetchCart();
        syncCartUI(canonical);
      } catch (syncErr) {
        showGlobalError(
          `Nu am putut resincroniza coșul: ${syncErr.message}`
        );
      }
    } finally {
      state.pending.delete(key);
      setBusy(row, false);
    }
  }

  root.addEventListener("click", (e) => {
    const row = e.target.closest("[data-cart-line]");
    if (!row) return;

    const input = row.querySelector("[data-qty-input]");
    const minus = e.target.closest("[data-qty-minus]");
    const plus = e.target.closest("[data-qty-plus]");
    const remove = e.target.closest("[data-cart-remove]");

    if (remove) {
      e.preventDefault();
      changeItem(row, 0);
      return;
    }

    if (!minus && !plus) return;

    e.preventDefault();

    const current = Number(input?.value || input?.min || 1);
    const step = Number(input?.step || 1) || 1;

    changeItem(row, current + (plus ? step : -step));
  });

  root.addEventListener("change", (e) => {
    const input = e.target.closest("[data-qty-input]");
    if (!input) return;

    changeItem(
      input.closest("[data-cart-line]"),
      Number(input.value)
    );
  });

  const stickyCheckout = root.querySelector(".anat-cart__checkout--sticky");
  if (stickyCheckout && stickyCheckout.parentElement !== document.body) {
    document.body.appendChild(stickyCheckout);
  }

  const checkoutButtons = [
    ...root.querySelectorAll("[data-native-checkout]"),
    ...(stickyCheckout ? [stickyCheckout] : [])
  ].filter((button, index, list) => list.indexOf(button) === index);

  checkoutButtons.forEach((checkout) => checkout.addEventListener("click", (e) => {
    e.preventDefault();

    if (state.checkoutPending) return;

    state.checkoutPending = true;
    checkoutButtons.forEach((button) => {
      button.setAttribute("aria-disabled", "true");
      button.classList.add("is-loading");
      button.textContent = "SE DESCHIDE CHECKOUT-UL…";
    });

    // Normal Shopify checkout.
    // Releasit injects and manages its own separate buttons/forms.
    window.location.assign(route("checkout"));
  }));

  fetchCart()
    .then(syncCartUI)
    .catch((err) => {
      showGlobalError(`Inițializare coș: ${err.message}`);
    });
})();
